'use client';

import React, { useEffect, useState, use } from 'react';
import { useUser } from '@clerk/nextjs';
import GoodMarkdownParser from '@/components/safeMarkedParser';
import { Link as LinkIcon, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Link from 'next/link';
import { createClient } from '@supabase/supabase-js';
import { useRouter } from 'next/navigation';
import { jsPDF } from 'jspdf';
import Markdown from '@/components/Markdown';

export default function ArticlePage({ params }: { params: Promise<{ slug: string }> }) {
    const { slug } = use(params);
    const { user, isLoaded } = useUser();
    const [policy, setPolicy] = useState<any>(null);
    const [articleIndex, setArticleIndex] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [revisionText, setRevisionText] = useState("");
    const [isAdmin, setIsAdmin] = useState(false);
    const [addingToDocId, setAddingToDocId] = useState<string | null>(null);
    const [chapters, setChapters] = useState<any[]>([]);
    const [prevArticle, setPrevArticle] = useState<any>(null);
    const [nextArticle, setNextArticle] = useState<any>(null);
    const [prevChapterLink, setPrevChapterLink] = useState<any>(null);
    const [nextChapterLink, setNextChapterLink] = useState<any>(null);
    const [viewMode, setViewMode] = useState<'English' | 'American'>('English');
    const [revisionTranslation, setRevisionTranslation] = useState("");
    const [showFootnotes, setShowFootnotes] = useState(false);

    const [selectedCategory, setSelectedCategory] = useState('');
    const [isEditingCategory, setIsEditingCategory] = useState(false);
    const [availableCategories, setAvailableCategories] = useState<string[]>(["Divine", "Moral", "General", "Rule"]);

    const ADMIN_ID = "user_3AtnbF82V4ROWYZ4rH2PlrSsvGk";


    const supabase = createClient(
        process.env.NEXT_PUBLIC_SUPABASE_URL!,
        process.env.NEXT_PUBLIC_SUPABASE_KEY!
    );

    const router = useRouter();

    const handleDownloadPDF = () => {
        const doc = new jsPDF();
        const width = doc.internal.pageSize.getWidth();
        const margin = 20;
        const maxWidth = width - (margin * 2);
        let yPos = 20;

        doc.setFont("Times New Roman")

        doc.setFontSize(22);
        const titleLines = doc.splitTextToSize(policy.title, maxWidth);
        doc.text(titleLines, margin, yPos);
        yPos += (titleLines.length * 10) + 5;

        doc.setFontSize(10);
        doc.setTextColor(100);
        const meta = `Article ${articleIndex}`;
        doc.text(meta, margin, yPos);
        yPos += 15;

        doc.setFontSize(12);
        doc.setTextColor(0);
        doc.setFont("Times New Roman")

        const allSections = [
            ...(policy.paragraphs || []),
            ...(policy.appendedSections || [])
        ].sort((a, b) => a.position - b.position);

        allSections.forEach((section, index) => {
            const prefix = section.position === 0 ? `Preamble: ` : `Section ${index + 1}. ${section.title && section.title} `;
            const content = `${prefix}${section.compiled_content}`;
            const heading = doc.splitTextToSize(prefix, maxWidth);
            const lines = doc.splitTextToSize(section.compiled_content, maxWidth);

            if (yPos + (lines.length * 7) > 280) {
                doc.addPage();
                yPos = 20;
            }

            doc.setFont("Times New Roman", "bold")

            doc.text(heading, margin, yPos);

            yPos += 10;

            doc.setFont("Times New Roman", "regular");

            doc.text(lines, margin, yPos);
            yPos += ((lines.length + heading.length) * 7) + 0;
        });

        doc.save(`${slug}.pdf`);
    };


    const [editingHeader, setEditingHeader] = useState(false);
    const [tempTitle, setTempTitle] = useState("");
    const [tempSlug, setTempSlug] = useState("");
    const [isUpdatingIsCoded, setIsUpdatingIsCoded] = useState(false);

    const handleUpdateHeader = async () => {

        const newSlug = tempTitle.toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '')
            || 'untitled';

        try {
            const { error } = await supabase
                .from('policy_documents')
                .update({
                    title: tempTitle,
                    slug: newSlug
                })
                .eq('id', policy.id);

            if (error) throw error;

            setEditingHeader(false);
            if (newSlug !== slug) {
                router.push(`/code/${newSlug}`);
            } else {
                fetchData();
            }
        } catch (error: any) {
            alert(error.message);
        }
    };


    const isEligibleAdmin = isLoaded && user?.id === ADMIN_ID;
    const showArticleUI = policy?.is_coded !== false;

    const handleToggleIsCoded = async (value: boolean) => {
        if (!policy) return;
        setIsUpdatingIsCoded(true);

        try {
            const { error } = await supabase
                .from('policy_documents')
                .update({ is_coded: value })
                .eq('id', policy.id);

            if (error) throw error;
            setPolicy({ ...policy, is_coded: value });
        } catch (error: any) {
            alert(error.message);
        } finally {
            setIsUpdatingIsCoded(false);
        }
    };

    const [titles, setTitles] = useState<any[]>([]);

    const handleUpdate = async (
        targetId: string,
        isAmendment: boolean = false
    ) => {
        try {
            const tableName = isAmendment ? 'policy_updates' : 'policy_paragraphs';

            const { error } = await supabase
                .from(tableName)
                .update({
                    content: revisionText,
                    translation: revisionTranslation,
                    compiled_content: revisionText
                })
                .eq('id', targetId);

            if (error) throw error;

            setEditingId(null);
            setRevisionText("");
            setRevisionTranslation("");
            fetchData();
        } catch (error: any) {
            alert(error.message);
        }
    };


    const fetchData = async () => {
        try {
            const [listRes, docRes, chapterRes, titlesRes] = await Promise.all([
                fetch(`/api/bills/effective/list`, { cache: `no-store` }),
                fetch(`/api/bills/effective/numbered/${slug}`, { cache: `no-store` }),
                fetch(`/api/bills/chapters/full/${titleChapter}`, { cache: `no-store` }),
                fetch('/api/bills/titles/list', { cache: 'no-store' })
            ]);

            const listData = await listRes.json();
            const docData = await docRes.json();
            const chaptersData = await chapterRes.json();
            const titlesData = await titlesRes.json();

            setChapters(chaptersData);

            if (Array.isArray(titlesData)) {
                const sortedTitles = [...titlesData].sort((a, b) => (a.position || 0) - (b.position || 0));
                setTitles(sortedTitles);
            }


            if (docData && !docData.error) {
                setPolicy(docData);
                setSelectedCategory(docData?.category)
                //  console.log(policy?.category)


                if (Array.isArray(listData)) {
                    // 1. Sort the entire global list by position/creation
                    const globalSorted = [...listData].sort((a, b) => {
                        if (a.position != null && b.position != null) return a.position - b.position;
                        if (a.position != null) return -1;
                        if (b.position != null) return 1;
                        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
                    });

                    // 2. Identify Current Location
                    const currentTag = docData.tags?.[0];
                    const currentChapterIdx = chaptersData.findIndex((c: any) => c.name === currentTag);
                    const isMisc = currentChapterIdx === -1;

                    // 3. Filter for local article navigation (within the same chapter)
                    const chapterPolicies = globalSorted.filter(p => {
                        const pTag = p.tags?.[0];
                        const pIsMisc = !pTag || !chaptersData.find((c: any) => c.name === pTag);
                        return isMisc ? pIsMisc : pTag === currentTag;
                    });

                    const index = chapterPolicies.findIndex(p => p.id === docData.id);
                    setArticleIndex(index + 1);
                    setPrevArticle(chapterPolicies[index - 1] || null);
                    setNextArticle(chapterPolicies[index + 1] || null);

                    // 4. Chapter Navigation Logic
                    const getFirstInChapter = (tagName: string) => globalSorted.find(p => p.tags?.[0] === tagName);
                    const getFirstInGeneral = () => globalSorted.find(p => {
                        const pTag = p.tags?.[0];
                        return !pTag || !chaptersData.find((c: any) => c.name === pTag);
                    });

                    // Handle Previous Chapter Link
                    if (!isMisc) {
                        if (currentChapterIdx > 0) {
                            const name = chaptersData[currentChapterIdx - 1].name;
                            const firstArt = getFirstInChapter(name);
                            if (firstArt) setPrevChapterLink({ slug: firstArt.slug, name });
                        }
                    } else if (chaptersData.length > 0) {
                        const name = chaptersData[chaptersData.length - 1].name;
                        const firstArt = getFirstInChapter(name);
                        if (firstArt) setPrevChapterLink({ slug: firstArt.slug, name });
                    }

                    // Handle Next Chapter Link
                    if (!isMisc) {
                        if (currentChapterIdx < chaptersData.length - 1) {
                            const name = chaptersData[currentChapterIdx + 1].name;
                            const firstArt = getFirstInChapter(name);
                            if (firstArt) setNextChapterLink({ slug: firstArt.slug, name });
                        } else {
                            const firstGen = getFirstInGeneral();
                            if (firstGen) setNextChapterLink({ slug: firstGen.slug, name: `General` });
                        }
                    }
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        setSelectedCategory(policy?.category)
        console.log(policy?.category)
    }, [slug]);

    useEffect(() => {
        if (!loading && policy) {
            const hash = window.location.hash;
            if (hash) {
                const id = hash.replace('#', '');
                setTimeout(() => {
                    const element = document.getElementById(id);
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                    }
                }, 100);
            }
        }
    }, [loading, policy]);

    const [titleChapter, setTitleChapters] = useState([])

    useEffect(() => {

    }, [])

    const [loadingTitles, setLoadingTitles] = useState(true);

    useEffect(() => {
        if (!chapters) return;

        const fetchTitleChapters = async () => {

            setLoadingTitles(true);

            const tag = policy?.tags?.[0];
            const cIndex = chapters.findIndex(c => c.name === tag);

            if (!chapters[cIndex]?.title_name) {
                if (!policy) return;
                setLoadingTitles(false);
                return;
            }

            const [chapterRes] = await Promise.all([
                fetch(`/api/bills/chapters/title/${chapters[cIndex].title_name ?? "Supplement"}`, { cache: `no-store` }),
            ]);

            let chaptersData;

            if (chapters[cIndex].title_name) { chaptersData = await chapterRes.json(); }

            setTitleChapters(chaptersData);

            setLoadingTitles(false);


        }

        fetchTitleChapters();

    }, [chapters])


    async function handleUpdateCategory() {
        const { error } = await supabase
            .from('policy_documents')
            .update({ category: selectedCategory })
            .eq('id', policy.id);

        if (!error) {
            setPolicy({ ...policy, category: selectedCategory });
            setIsEditingCategory(false);
        }
    }

    const handleUpdateCode = async (
        paraId: string | null,
        contentOverride?: string,
        isInternalOverride?: boolean,
        isNew: boolean = false,
        docId?: string,
        position?: number,
        updateId?: string
    ) => {
        try {
            const content = contentOverride !== undefined ? contentOverride : revisionText;

            if (paraId && !isNew) {
                // Update existing paragraph
                const { error } = await supabase
                    .from('policy_paragraphs')
                    .update({
                        content,
                        translation: revisionTranslation,
                        title: revisionTitle,
                    })
                    .eq('id', paraId);

                if (error) throw error;
            } else if (updateId) {
                // Update existing amendment
                const { error } = await supabase
                    .from('policy_updates')
                    .update({
                        content,
                        translation: revisionTranslation,
                        compiled_content: content
                    })
                    .eq('id', updateId);

                if (error) throw error;
            } else if (isNew && docId) {
                // Create new amendment
                const { error } = await supabase
                    .from('policy_updates')
                    .insert({
                        policy_document_id: docId,
                        content,
                        translation: revisionTranslation,
                        compiled_content: content,
                        position: position || 0
                    });

                if (error) throw error;
            }

            setEditingId(null);
            setRevisionText("");
            setRevisionTranslation("");
            fetchData();
        } catch (error: any) {
            alert(error.message);
        }
    };

    const copyCitation = (articleNum: number, sectionNum: number, anchorId: string) => {
        const url = `${window.location.origin}${window.location.pathname}#${anchorId}`;
        navigator.clipboard.writeText(url);
        setCopiedId(anchorId);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const [revisionTitle, setRevisionTitle] = useState('');

    if (loading || loadingTitles) return <div className="min-h-screen bg-amber-50 flex items-center justify-center font-serif italic text-slate-500">Retrieving Official Records...</div>;
    if (!policy) return <div className="min-h-screen bg-amber-50 flex items-center justify-center font-serif text-slate-500">Article Not Found</div>;

    return (
        <div className="min-h-screen bg-amber-50 bg-[#fcfaf7] text-slate-900 font-serif selection:bg-amber-200">
            <div className="max-w-5xl mx-auto px-8 py-20">
                <header className=" -mt-10 pb-10">
                    <Link href="/code" className="text-xs mb-3 font-sans font-bold uppercase text-amber-800 hover:underline flex items-center gap-1">
                        <ChevronLeft size={14} /> Back to Code
                    </Link>

                    <div className="flex flex-col gap-4">
                        {/* Chapter Navigation Row */}
                        {showArticleUI && (prevChapterLink || nextChapterLink) && false && (
                            <div className="flex items-center justify-between border-b border-slate-200 pb-3 mb-1">
                                <div className="flex-1">
                                    {prevChapterLink && (
                                        <Link
                                            href={`/code/${prevChapterLink.slug}`}
                                            className="group flex flex-col gap-0.5"
                                        >
                                            <span className="text-[9px] font-sans font-bold uppercase tracking-[0.1em] text-slate-400 group-hover:text-amber-800 transition-colors">Previous Chapter</span>
                                            <span className="text-xs font-sans font-bold text-slate-600 group-hover:text-amber-800 transition-colors flex items-center gap-1">
                                                <ChevronLeft size={14} /> {prevChapterLink.name}
                                            </span>
                                        </Link>
                                    )}
                                </div>

                                <div className="flex-1 text-right">
                                    {nextChapterLink && (
                                        <Link
                                            href={`/code/${nextChapterLink.slug}`}
                                            className="group flex flex-col gap-0.5"
                                        >
                                            <span className="text-[9px] font-sans font-bold uppercase tracking-[0.1em] text-slate-400 group-hover:text-amber-800 transition-colors">Next Chapter</span>
                                            <span className="text-xs font-sans font-bold text-slate-600 group-hover:text-amber-800 transition-colors flex items-center gap-1 justify-end">
                                                {nextChapterLink.name} <ChevronRight size={14} />
                                            </span>
                                        </Link>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Article Navigation Row */}
                        {showArticleUI && (
                            <div className="flex items-center justify-end">
                                <div className="flex items-center gap-6">
                                    {prevArticle && (
                                        <Link
                                            href={`/code/${prevArticle.slug}`}
                                            className="text-slate-400 hover:text-amber-800 transition-colors flex items-center gap-1 text-[10px] font-sans font-bold uppercase tracking-widest"
                                            title={prevArticle.title}
                                        >
                                            <ChevronLeft size={16} /> Previous Article
                                        </Link>
                                    )}
                                    {nextArticle && (
                                        <Link
                                            href={`/code/${nextArticle.slug}`}
                                            className="text-slate-400 hover:text-amber-800 transition-colors flex items-center gap-1 text-[10px] font-sans font-bold uppercase tracking-widest"
                                            title={nextArticle.title}
                                        >
                                            Next Article <ChevronRight size={16} />
                                        </Link>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Title and Admin Row */}
                        <div className='flex flex-row justify-between items-center'>
                            {showArticleUI && (
                                <h3 className="text-sm font-sans font-bold uppercase text-slate-900 tracking-widest">
                                    {(() => {
                                        const tag = policy.tags?.[0];
                                        const cIndex = chapters.findIndex(c => c.name === tag);
                                        const newCIndex = titleChapter.findIndex(c => c.name === tag);
                                        const tIndex = titles.findIndex(t => t.name === chapters[cIndex]?.title_name ?? "Supplement")

                                        if (tag && cIndex !== -1) {
                                            return `Title${tIndex !== 0 && " " + tIndex}: ${chapters[cIndex].title_name}, Chapter ${newCIndex + 1}: ${tag}, Article ${articleIndex}`;
                                        }

                                        return `Chapter ${chapters.length + 1}: General, Article ${articleIndex}`;
                                    })()}
                                </h3>
                            )}

                            <div className="flex items-center gap-2">
                                {isAdmin && false && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-sans">Coding</span>
                                        <select
                                            className="text-[10px] border border-slate-300 rounded bg-white px-2 py-1"
                                            value={showArticleUI ? 'true' : 'false'}
                                            onChange={(e) => handleToggleIsCoded(e.target.value === 'true')}
                                            disabled={isUpdatingIsCoded}
                                        >
                                            <option value="true">Coded</option>
                                            <option value="false">Uncoded</option>
                                        </select>
                                    </div>
                                )}

                                {isAdmin && (
                                    <button
                                        onClick={handleDownloadPDF}
                                        className="px-4 py-2 bg-slate-800 text-white text-[10px] font-sans font-bold uppercase rounded shadow-sm hover:bg-slate-700 transition-colors flex items-center gap-2"
                                    >
                                        Download Official PDF
                                    </button>
                                )}



                                <div className="flex gap-2">
                                    {isEligibleAdmin && !isAdmin && (
                                        <button
                                            onClick={() => setIsAdmin(true)}
                                            className="p-3 rounded-md bg-slate-800 text-white text-xs font-bold uppercase font-sans w-fit"
                                        >
                                            Enter Admin
                                        </button>
                                    )}
                                    {isAdmin && (
                                        <button
                                            onClick={() => setIsAdmin(false)}
                                            className="p-3 rounded-md bg-orange-300 text-orange-900 text-xs font-bold uppercase font-sans w-fit"
                                        >
                                            Leave Admin
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </header>

                <main className="space-y-32">
                    <section className="scroll-mt-10">
                        <div className="mb-12 -mt-6 border-l-8 border-amber-800 pl-8 bg-amber-50 p-6 rounded-r-lg">
                            {showArticleUI && (
                                <span className="font-sans font-bold text-amber-800 uppercase tracking-widest text-sm">Article {articleIndex}</span>
                            )}
                            {isAdmin && !editingHeader && (
                                <button
                                    onClick={() => {
                                        setEditingHeader(true);
                                        setTempTitle(policy.title);
                                        setTempSlug(slug);
                                    }}
                                    className="text-[10px] font-sans font-bold uppercase text-blue-600 hover:underline mb-2 block"
                                >
                                    Edit Title
                                </button>
                            )}

                            {editingHeader ? (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-[10px] font-sans font-bold uppercase text-slate-500">Title</label>
                                        <input
                                            className="w-full p-2 border border-amber-300 rounded font-serif text-xl"
                                            value={tempTitle}
                                            onChange={(e) => setTempTitle(e.target.value)}
                                        />
                                    </div>

                                    <div className="flex gap-2 mb-3">
                                        <button onClick={handleUpdateHeader} className="bg-slate-800 text-white px-3 py-1 text-xs font-sans rounded">Save Header</button>
                                        <button onClick={() => setEditingHeader(false)} className="text-slate-500 text-xs font-sans">Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                <h2 className="text-4xl font-bold mt-2 mb-4 leading-tight text-slate-900">{policy.title}</h2>
                            )}
                            <div className="flex">

                                {isEditingCategory ? (
                                    <div className="flex items-center gap-2">
                                        <select
                                            className="text-xs p-1 border border-stone-300 rounded bg-white outline-none"
                                            value={selectedCategory}
                                            onChange={(e) => setSelectedCategory(e.target.value)}
                                        >
                                            <option value="">None</option>
                                            {availableCategories.map((cat) => (
                                                <option key={cat} value={cat}>{cat === "Moral" ? "Natural" : cat === "General" ? "Human" : cat === "Rule" ? "Political" : cat}</option>
                                            ))}
                                        </select>
                                        <button onClick={handleUpdateCategory} className="text-[10px] bg-green-600 text-white px-2 py-1 rounded uppercase font-bold">Save</button>
                                        <button onClick={() => setIsEditingCategory(false)} className="text-[10px] bg-stone-200 px-2 py-1 rounded uppercase font-bold">Cancel</button>
                                    </div>
                                ) : (
                                    <>
                                        {policy.category && (
                                            <div className={`text-white font-sans px-2 font-bold text-xs py-1 rounded
                                    ${policy.category === 'Moral' ? 'bg-red-800' : ''}
                                    ${policy.category === 'General' ? 'bg-blue-800' : ''}
                                    ${policy.category === 'Divine' ? 'bg-purple-800' : ''}
                                    ${policy.category === 'Introductory' ? 'bg-orange-800' : ''}
                                    ${!['Moral', 'General', 'Divine'].includes(policy.category) ? 'bg-slate-700' : ''}`}>
                                                {policy.category === 'General' ? 'Human' : policy.category === "Divine" ? "Eternal" : policy.category === "Moral" ? "Natural" : policy.category === "Rule" ? "Political" : policy.category === "Opinion" ? "Political" : policy.category}
                                            </div>
                                        )}
                                        {isAdmin && (
                                            <button
                                                onClick={() => setIsEditingCategory(true)}
                                                className="text-[10px] ml-2 text-amber-800 hover:underline font-bold uppercase"
                                            >
                                                Change Category
                                            </button>
                                        )}
                                    </>
                                )}

                                {isAdmin && false && (
                                    <button
                                        onClick={async () => {
                                            await fetch('/api/bills/toggle-document', {
                                                method: 'POST',
                                                body: JSON.stringify({
                                                    documentId: policy.id,
                                                    tags: policy.tags,
                                                    adminId: ADMIN_ID,
                                                    makeInternal: true
                                                })
                                            });
                                            window.location.href = '/code';
                                        }}
                                        className="ml-4 text-xs border border-red-200 px-2 py-1 text-red-600 hover:bg-red-50"
                                    >
                                        Archive Entire Article
                                    </button>
                                )}

                            </div>


                        </div>
                        {isAdmin && false && (
                            <div className="flex  mb-3 -mt-8">
                                <div className="inline-flex p-1 bg-slate-200 rounded-lg">
                                    <button
                                        onClick={() => setViewMode('English')}
                                        className={`px-4 py-2 text-xs font-sans font-bold uppercase rounded-md transition-all ${viewMode === 'English' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                    >
                                        English
                                    </button>
                                    <button
                                        onClick={() => setViewMode('American')}
                                        className={`px-4 py-2 text-xs font-sans font-bold uppercase rounded-md transition-all ${viewMode === 'American' ? 'bg-amber-800 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                    >
                                        American
                                    </button>
                                </div>
                            </div>
                        )}

                        <button
                            onClick={() => setShowFootnotes(prev => !prev)}
                            className="px-2 mb-3 -mt-7 py-1 bg-slate-600 text-white text-[10px] font-sans font-bold uppercase rounded shadow-sm hover:bg-slate-700 transition-colors flex items-center gap-2"
                        >
                            {showFootnotes ? "Hide" : "Show"} Notes
                        </button>

                        {(() => {
                            const descriptionSection = policy.paragraphs?.find((p: any) => p.position === 0);
                            const normalParagraphs = policy.paragraphs?.filter((p: any) => p.position !== 0) || [];

                            return (
                                <>
                                    {descriptionSection && (
                                        <div className="mb-10 rounded-3xl border border-amber-300 bg-amber-100/80 p-6 shadow-lg">
                                            <div className="flex flex-col gap-4">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.3em] text-amber-800">
                                                            Preamble
                                                        </div>
                                                    </div>
                                                    {isAdmin && editingId !== descriptionSection.id && (
                                                        <button onClick={() => { setEditingId(descriptionSection.id); setRevisionText(descriptionSection.compiled_content); setRevisionTranslation(descriptionSection.translation || ""); }} className="font-sans font-bold text-[10px] uppercase tracking-widest text-blue-600 hover:text-blue-800">Edit Preamble</button>
                                                    )}
                                                </div>
                                            </div>
                                            {editingId === descriptionSection.id ? (
                                                <div className="mt-6 space-y-4">
                                                    <div>
                                                        <label className="block text-xs font-sans font-bold text-slate-600 mb-2">Content</label>
                                                        <textarea className="w-full p-4 font-sans text-base border-2 border-amber-300 rounded" rows={8} value={revisionText} onChange={(e) => setRevisionText(e.target.value)} />
                                                    </div>

                                                    <div className="flex gap-2">
                                                        <button onClick={() => handleUpdateCode(descriptionSection.id)} className="bg-slate-800 text-white px-4 py-2 text-xs font-sans">Save Preamble</button>
                                                        <button onClick={() => { setEditingId(null); setRevisionText(""); setRevisionTranslation(""); }} className="text-slate-500 text-xs font-sans">Cancel</button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="mt-3 -mb-6 prose prose-slate max-w-none prose-lg leading-relaxed text-slate-800">
                                                    <ReactMarkdown
                                                        remarkPlugins={[remarkGfm]}
                                                        components={{
                                                            h1: ({ ...props }) => <h1 className="text-3xl font-bold mt-0 mb-6 border-b pb-2" {...props} />,
                                                            h2: ({ ...props }) => <h2 className="text-2xl font-bold mt-10 mb-4" {...props} />,
                                                            h3: ({ ...props }) => <h3 className="text-xl font-bold mt-8 mb-4" {...props} />,
                                                            h4: ({ ...props }) => <h4 className="text-lg font-bold mt-6 mb-2" {...props} />,
                                                            p: ({ ...props }) => <p className="mb-6 leading-relaxed block" {...props} />,
                                                            ul: ({ ...props }) => <ul className="list-disc ml-6 mb-6 space-y-2" {...props} />,
                                                            ol: ({ ...props }) => <ol className="list-decimal ml-6 mb-6 space-y-2" {...props} />,
                                                            li: ({ ...props }) => <li className="mb-1" {...props} />,
                                                            table: ({ ...props }) => (
                                                                <div className="overflow-x-auto my-8">
                                                                    <table className="min-w-full border-collapse border border-gray-300 text-sm" {...props} />
                                                                </div>
                                                            ),
                                                            thead: ({ ...props }) => <thead className="bg-gray-100" {...props} />,
                                                            th: ({ ...props }) => <th className="border border-gray-300 p-3 text-left font-bold" {...props} />,
                                                            td: ({ ...props }) => <td className="border border-gray-300 p-3" {...props} />,
                                                            tr: ({ ...props }) => <tr className="even:bg-gray-50" {...props} />,
                                                            strong: ({ ...props }) => <strong className="font-bold text-black" {...props} />,

                                                        }} >
                                                        {viewMode === 'American' && descriptionSection.translation ? descriptionSection.translation : descriptionSection.compiled_content}
                                                    </ReactMarkdown>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="space-y-8 border-t border-slate-300 pt-8">
                                        {normalParagraphs.map((para: any, pIdx: number) => {
                                            //const anchorId = `A-${articleIndex}-S-${pIdx + 1}`;
                                            const anchorId = `S-${pIdx + 1}`;
                                            const isCopied = copiedId === anchorId;
                                            const displayContent = viewMode === 'American' && para.translation ? para.translation : para.compiled_content;
                                            const paraTitle = para.title ? para.title : "";
                                            const sectionNum = policy.isBeginning ? pIdx + 1 : (policy.globalOffset || 0) + pIdx + 1;

                                            return (
                                                <div key={para.id} id={anchorId} className="group relative scroll-mt-20 border-b border-slate-200 pb-0 last:border-0">
                                                    <div className="flex items-start justify-between -mb-2">

                                                        {/*<div className="flex items-center gap-4">
                                                {true && editingId !== para.id && (
                                                    <button onClick={() => { setEditingId(para.id); setRevisionText(para.compiled_content); setRevisionTranslation(para.translation || ""); }} className="opacity-0 group-hover:opacity-100 transition-opacity font-sans font-bold text-[10px] uppercase tracking-widest text-blue-600 hover:text-blue-800">Edit</button>
                                                )}
                                                <button
                                                    onClick={() => copyCitation(articleIndex || 0, pIdx + 1, anchorId)}
                                                    className={`transition-all flex items-center gap-2 font-sans font-bold text-[10px] uppercase tracking-widest ${isCopied ? 'text-green-600 opacity-100' : 'opacity-0 group-hover:opacity-100 text-slate-500 hover:text-amber-800'}`}
                                                >
                                                    {isCopied ? <Check size={12} /> : <LinkIcon size={12} />}
                                                    {isCopied ? 'Cited' : 'Cite'}
                                                </button>
                                            </div>*/}

                                                    </div>

                                                    <div className="prose prose-slate  max-w-none prose-lg md:prose-xl leading-extra-loose text-justify text-slate-800">
                                                        {editingId === para.id ? (
                                                            <div className="space-y-4">
                                                                <div>
                                                                    <label className="block text-md font-bold text-slate-900 mb-2">Title</label>
                                                                    <textarea className="w-full p-4 font-sans text-base border-2 border-amber-300 resize-none rounded" rows={1} value={revisionTitle} onChange={(e) => setRevisionTitle(e.target.value)} />
                                                                </div>
                                                                <div>
                                                                    <label className="block text-xs font-sans font-bold text-slate-600 mb-2">Content</label>
                                                                    <textarea className="w-full p-4 font-sans text-base border-2 border-amber-300 rounded" rows={6} value={revisionText} onChange={(e) => setRevisionText(e.target.value)} />
                                                                </div>

                                                                <div className="flex mb-3 gap-2">
                                                                    <button onClick={() => handleUpdateCode(para.id)} className="bg-slate-800 text-white px-4 py-2 text-xs font-sans">Save Changes</button>
                                                                    <button onClick={() => { setEditingId(null); setRevisionText(""); setRevisionTranslation(""); setRevisionTitle(""); }} className="text-slate-500 text-xs font-sans">Cancel</button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className={`flex gap-4 items-start ${!para.title ? "" : "flex-col"}`}>
                                                                <div className='flex items-start space-between justify-between'>
                                                                    <Link href={`/code/${slug}/section/${sectionNum}`} className={`hover:bg-amber-900 hovaer:underline bg-amber-800 ${!para.title && "translate-y-0"} h-fit mt-0 text-white font-sans font-bold text-sm px-2 py-0.5 rounded shrink-0`}>
                                                                        <span >§{sectionNum}{para.title && `. ${para.title}`}</span>
                                                                    </Link>

                                                                    {para.title &&
                                                                        <div className="flex items-center gap-4 translate-y-[2px] ml-3">
                                                                            {isAdmin && editingId !== para.id && (
                                                                                <button onClick={() => { setEditingId(para.id); setRevisionText(para.compiled_content); setRevisionTitle(para.title); setRevisionTranslation(para.translation || ""); }} className="opacity-0 group-hover:opacity-100 transition-opacity font-sans font-bold text-[10px] uppercase tracking-widest text-blue-600 hover:text-blue-800">Edit</button>
                                                                            )}
                                                                            <button
                                                                                onClick={() => copyCitation(articleIndex || 0, pIdx + 1, anchorId)}
                                                                                className={`transition-all flex items-center gap-2 font-sans font-bold text-[10px] uppercase tracking-widest ${isCopied ? 'text-green-600 opacity-100' : 'opacity-0 group-hover:opacity-100 text-slate-500 hover:text-amber-800'}`}
                                                                            >
                                                                                {isCopied ? <Check size={12} /> : <LinkIcon size={12} />}
                                                                                {isCopied ? 'Cited' : 'Cite'}
                                                                            </button>
                                                                        </div>
                                                                    }

                                                                </div>
                                                                <div className={`${!para.title ? "flex-1 min-w-0" : "flex-1 min-w-0"}`}> {/* flex-1 min-w-0 */}
                                                                    <Markdown content={displayContent as string} showFootnotes={showFootnotes} />
                                                                </div>
                                                                {!para.title && para.title !== "" && (
                                                                    <div className="flex items-center gap-4">
                                                                        {isAdmin && editingId !== para.id && (
                                                                            <button onClick={() => { setEditingId(para.id); setRevisionText(para.compiled_content); setRevisionTitle(para.title); setRevisionTranslation(para.translation || ""); }} className="opacity-0 group-hover:opacity-100 transition-opacity font-sans font-bold text-[10px] uppercase tracking-widest text-blue-600 hover:text-blue-800">Edit</button>
                                                                        )}
                                                                        <button
                                                                            onClick={() => copyCitation(articleIndex || 0, pIdx + 1, anchorId)}
                                                                            className={`transition-all flex items-center gap-2 font-sans font-bold text-[10px] uppercase tracking-widest ${isCopied ? 'text-green-600 opacity-100' : 'opacity-0 group-hover:opacity-100 text-slate-500 hover:text-amber-800'}`}
                                                                        >
                                                                            {isCopied ? <Check size={12} /> : <LinkIcon size={12} />}
                                                                            {isCopied ? 'Cited' : 'Cite'}
                                                                        </button>

                                                                    </div>
                                                                )}
                                                                {/* <div className="flex items-center gap-4 -mb-3 -translate-y-6">
                                                                    {isAdmin && editingId !== para.id && (
                                                                        <button onClick={() => { setEditingId(para.id); setRevisionText(para.compiled_content); setRevisionTitle(para.title); setRevisionTranslation(para.translation || ""); }} className="opacity-0 group-hover:opacity-100 transition-opacity font-sans font-bold text-[10px] uppercase tracking-widest text-blue-600 hover:text-blue-800">Edit</button>
                                                                    )}
                                                                    <button
                                                                        onClick={() => copyCitation(articleIndex || 0, pIdx + 1, anchorId)}
                                                                        className={`transition-all flex items-center gap-2 font-sans font-bold text-[10px] uppercase tracking-widest ${isCopied ? 'text-green-600 opacity-100' : 'opacity-0 group-hover:opacity-100 text-slate-500 hover:text-amber-800'}`}
                                                                    >
                                                                        {isCopied ? <Check size={12} /> : <LinkIcon size={12} />}
                                                                        {isCopied ? 'Cited' : 'Cite'}
                                                                    </button>
                                                                </div> */}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        {policy.appendedSections?.map((section: any, sIdx: number) => {
                                            const anchorId = `amendment-${section.id}`;
                                            const isCopied = copiedId === anchorId;
                                            const displayContent = viewMode === 'American' && section.translation ? section.translation : section.compiled_content;

                                            return (
                                                <div key={section.id} id={anchorId} className="group relative border-b border-slate-200 pb-12 mt-16 scroll-mt-20">
                                                    <div className="flex items-start justify-between mb-6">
                                                        <div className="bg-amber-800 text-white w-fit px-2 py-1 text-[10px] font-sans font-bold uppercase tracking-wider rounded">
                                                            {showArticleUI
                                                                ? `Article ${articleIndex} § ${policy.paragraphs.length + sIdx + 1} (Amendment)`
                                                                : `§ ${policy.paragraphs.length + sIdx + 1} (Amendment)`}
                                                        </div>

                                                        <div className="flex items-center gap-4">
                                                            {isAdmin && editingId !== section.id && (
                                                                <button onClick={() => { setEditingId(section.id); setRevisionText(section.compiled_content); setRevisionTranslation(section.translation || ""); }} className="opacity-0 group-hover:opacity-100 transition-opacity font-sans font-bold text-[10px] uppercase tracking-widest text-blue-600 hover:text-blue-800">Edit Amendment</button>
                                                            )}
                                                            <button onClick={() => copyCitation(articleIndex || 0, policy.paragraphs.length + sIdx + 1, anchorId)} className={`transition-all flex items-center gap-2 font-sans font-bold text-[10px] uppercase tracking-widest ${isCopied ? 'text-green-600 opacity-100' : 'opacity-0 group-hover:opacity-100 text-slate-500 hover:text-amber-800'}`}>
                                                                {isCopied ? <Check size={12} /> : <LinkIcon size={12} />}
                                                                {isCopied ? 'Cited' : 'Cite Amendment'}
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <div className="prose prose-slate max-w-none prose-lg md:prose-xl leading-extra-loose text-justify text-slate-800">
                                                        {editingId === section.id ? (
                                                            <div className="space-y-4">
                                                                <div>
                                                                    <label className="block text-xs font-sans font-bold text-slate-600 mb-2">English Content</label>
                                                                    <textarea className="w-full p-4 font-sans text-base border-2 border-amber-300 rounded" rows={6} value={revisionText} onChange={(e) => setRevisionText(e.target.value)} />
                                                                </div>
                                                                {isAdmin && (
                                                                    <div>
                                                                        <label className="block text-xs font-sans font-bold text-slate-600 mb-2">American Translation</label>
                                                                        <textarea className="w-full p-4 font-sans text-base border-2 border-blue-300 rounded" rows={6} value={revisionTranslation} onChange={(e) => setRevisionTranslation(e.target.value)} placeholder="..." />
                                                                    </div>
                                                                )}
                                                                <div className="flex gap-2">
                                                                    <button onClick={() => handleUpdateCode(null, undefined, false, true, policy.id, section.position, section.id)} className="bg-slate-800 text-white px-4 py-2 text-xs font-sans">Save Amendment</button>
                                                                    <button onClick={() => { setEditingId(null); setRevisionText(""); setRevisionTranslation(""); }} className="text-slate-500 text-xs font-sans">Cancel</button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <GoodMarkdownParser content={displayContent} />
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        {/* {false && (
                                <div className="mt-12">
                                    {addingToDocId === policy.id ? (
                                        <div className="p-6 bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg space-y-4">
                                            <div>
                                                <label className="block text-xs font-sans font-bold text-slate-600 mb-2">English Content</label>
                                                <textarea className="w-full p-4 border-2 border-amber-300 rounded" rows={4} placeholder="Enter text here..." value={revisionText} onChange={(e) => setRevisionText(e.target.value)} />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-sans font-bold text-slate-600 mb-2">American Translation (Optional)</label>
                                                <textarea className="w-full p-4 border-2 border-blue-300 rounded" rows={4} placeholder="..." value={revisionTranslation} onChange={(e) => setRevisionTranslation(e.target.value)} />
                                            </div>
                                            <div className="flex gap-2">
                                                <button onClick={() => { const nextPos = (policy.appendedSections?.length || 0) + 1; handleUpdateCode(null, undefined, false, true, policy.id, nextPos); setAddingToDocId(null); }} className="bg-green-700 text-white px-4 py-2 text-xs font-sans font-bold uppercase">Publish</button>
                                                <button onClick={() => { setAddingToDocId(null); setRevisionText(""); setRevisionTranslation(""); }} className="text-slate-500 text-xs font-sans font-bold uppercase">Cancel</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <button onClick={() => { setAddingToDocId(policy.id); setRevisionText(""); setRevisionTranslation(""); }} className="w-full py-8 border-2 border-dashed border-slate-200 text-slate-400 hover:text-slate-600 hover:border-slate-400 hover:bg-slate-50 transition-all font-sans font-bold uppercase text-xs tracking-[0.2em]">+ Append New Section</button>
                                    )}
                                </div>
                            )} */}
                                    </div>
                                </>
                            );
                        })()}
                    </section>
                </main>
            </div>
        </div>
    );
}


/* {policy.appendedSections?.map((section: any, sIdx: number) => {
                                const anchorId = `amendment-${section.id}`;
                                const isCopied = copiedId === anchorId;
                                const displayContent = viewMode === 'American' && section.translation ? section.translation : section.compiled_content;

                                return (
                                    <div key={section.id} id={anchorId} className="group relative border-b border-slate-200 pb-12 mt-16 scroll-mt-20">
                                        <div className="flex items-start justify-between mb-6">
                                            <div className="bg-amber-800 text-white w-fit px-2 py-1 text-[10px] font-sans font-bold uppercase tracking-wider rounded">
                                                            {showArticleUI
                                                                ? `Article ${articleIndex} § ${policy.paragraphs.length + sIdx + 1} (Amendment)`
                                                                : `§ ${policy.paragraphs.length + sIdx + 1} (Amendment)`}

                                            <div className="flex items-center gap-4">
                                                {isAdmin && editingId !== section.id && (
                                                    <button onClick={() => { setEditingId(section.id); setRevisionText(section.compiled_content); setRevisionTranslation(section.translation || ""); }} className="opacity-0 group-hover:opacity-100 transition-opacity font-sans font-bold text-[10px] uppercase tracking-widest text-blue-600 hover:text-blue-800">Edit Amendment</button>
                                                )}
                                                <button onClick={() => copyCitation(articleIndex || 0, policy.paragraphs.length + sIdx + 1, anchorId)} className={`transition-all flex items-center gap-2 font-sans font-bold text-[10px] uppercase tracking-widest ${isCopied ? 'text-green-600 opacity-100' : 'opacity-0 group-hover:opacity-100 text-slate-500 hover:text-amber-800'}`}>
                                                    {isCopied ? <Check size={12} /> : <LinkIcon size={12} />}
                                                    {isCopied ? 'Cited' : 'Cite Amendment'}
                                                </button>
                                            </div>
                                        </div>

                                        <div className="prose prose-slate max-w-none prose-lg md:prose-xl leading-extra-loose text-justify text-slate-800">
                                            {editingId === section.id ? (
                                                <div className="space-y-4">
                                                    <div>
                                                        <label className="block text-xs font-sans font-bold text-slate-600 mb-2">English Content</label>
                                                        <textarea className="w-full p-4 font-sans text-base border-2 border-amber-300 rounded" rows={6} value={revisionText} onChange={(e) => setRevisionText(e.target.value)} />
                                                    </div>
                                                    {isAdmin && (
                                                        <div>
                                                            <label className="block text-xs font-sans font-bold text-slate-600 mb-2">American Translation</label>
                                                            <textarea className="w-full p-4 font-sans text-base border-2 border-blue-300 rounded" rows={6} value={revisionTranslation} onChange={(e) => setRevisionTranslation(e.target.value)} placeholder="..." />
                                                        </div>
                                                    )}
                                                    <div className="flex gap-2">
                                                        <button onClick={() => handleUpdateCode(null, undefined, false, true, policy.id, section.position, section.id)} className="bg-slate-800 text-white px-4 py-2 text-xs font-sans">Save Amendment</button>
                                                        <button onClick={() => { setEditingId(null); setRevisionText(""); setRevisionTranslation(""); }} className="text-slate-500 text-xs font-sans">Cancel</button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <GoodMarkdownParser content={displayContent} />
                                            )}
                                        </div>
                                    </div>
                                );
                            })} 

                            {isAdmin && (
                                <div className="mt-12">
                                    {addingToDocId === policy.id ? (
                                        <div className="p-6 bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg space-y-4">
                                            <div>
                                                <label className="block text-xs font-sans font-bold text-slate-600 mb-2">English Content</label>
                                                <textarea className="w-full p-4 border-2 border-amber-300 rounded" rows={4} placeholder="Enter text here..." value={revisionText} onChange={(e) => setRevisionText(e.target.value)} />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-sans font-bold text-slate-600 mb-2">American Translation (Optional)</label>
                                                <textarea className="w-full p-4 border-2 border-blue-300 rounded" rows={4} placeholder="..." value={revisionTranslation} onChange={(e) => setRevisionTranslation(e.target.value)} />
                                            </div>
                                            <div className="flex gap-2">
                                                <button onClick={() => { const nextPos = (policy.appendedSections?.length || 0) + 1; handleUpdateCode(null, undefined, false, true, policy.id, nextPos); setAddingToDocId(null); }} className="bg-green-700 text-white px-4 py-2 text-xs font-sans font-bold uppercase">Publish</button>
                                                <button onClick={() => { setAddingToDocId(null); setRevisionText(""); setRevisionTranslation(""); }} className="text-slate-500 text-xs font-sans font-bold uppercase">Cancel</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <button onClick={() => { setAddingToDocId(policy.id); setRevisionText(""); setRevisionTranslation(""); }} className="w-full py-8 border-2 border-dashed border-slate-200 text-slate-400 hover:text-slate-600 hover:border-slate-400 hover:bg-slate-50 transition-all font-sans font-bold uppercase text-xs tracking-[0.2em]">+ Append New Section</button>
                                    )}
                                </div>
                            )} */